// SET-assignment context scan — value-slot plan T15. Decides whether the
// `=` a recognized value slot hangs off is a DML SET assignment
// (UPDATE / MERGE WHEN-MATCHED UPDATE / INSERT … ON CONFLICT DO UPDATE →
// role 'set-rhs'), a non-DML SET statement (session variables, mysql
// GLOBAL/SESSION, T-SQL procedural `SET @v = |` → refuse the slot outright;
// G12: the clause machine calls ALL of these `update-set`, so this
// statement-head scan is the only layer that can tell them apart), or an
// ordinary comparison ('other' → the existing comparison-rhs role).
//
// Pure forward text scan (G11-rule: no tree-sitter), comment/string-aware,
// with a per-paren-depth "last clause keyword" stack so a comparison inside
// a SET-value subquery (`SET a = (SELECT … WHERE x = |)`) never reads as a
// SET assignment. Known accepted misses (fall back to 'other', i.e. today's
// clause-path behavior — never a wrong 'set-assignment'): assignments chained
// AFTER a CASE…END value (`END` clobbers the level keyword) and mysql's
// SET-less `ON DUPLICATE KEY UPDATE a = |`. An unquoted column literally
// named `set` can shadow the keyword — SET is reserved in every supported
// engine, so this stays a theoretical corner.

import {
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
} from './scanPrimitives';

export type SetAssignmentScanResult = 'set-assignment' | 'non-dml-set' | 'other';

/** Statement heads that can legally own a SET assignment list. */
const DML_SET_HEADS = new Set(['update', 'merge', 'insert']);

/** Depth-0 words that resolve the effective head behind a `WITH` CTE list. */
const CTE_BODY_HEADS = new Set(['update', 'merge', 'insert', 'delete', 'select']);

/** Clause keywords tracked per paren level. Only `set` opens the assignment
 *  list; every other entry exists to CLOSE it (or to keep CASE conditions and
 *  ON/WHERE predicates from reading as assignments). */
const TRACKED_KEYWORDS = new Set([
  'set', 'where', 'having', 'on', 'when', 'then', 'else', 'end', 'case',
  'select', 'from', 'values', 'returning', 'output', 'group', 'order', 'by',
  'limit', 'join', 'union', 'using',
]);

/** Scans `[from, until)` — `until` is the LHS-operand start of an already
 *  matched `=` — and classifies the assignment context. `engine` gates the
 *  quoted-identifier flavors, mirroring `scanValueTextState`. */
export function scanSetAssignmentContext(
  source: string,
  from: number,
  until: number,
  engine: string,
): SetAssignmentScanResult {
  let head: string | undefined;
  const levels: (string | undefined)[] = [undefined];
  let i = from;
  while (i < until) {
    const c = source.charCodeAt(i);
    if (c === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (c === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a) {
      i = skipBlockComment(source, i + 2);
      continue;
    }
    if (c === 0x27 /* ' */) {
      i = skipSingleQuoted(source, i + 1);
      continue;
    }
    if (c === 0x22 /* " */) {
      i = skipDoubleQuoted(source, i + 1);
      continue;
    }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b /* [ */) {
      i = skipBracketedId(source, i + 1);
      continue;
    }
    if (engine === 'mysql' && c === 0x60 /* ` */) {
      i = skipBacktickId(source, i + 1);
      continue;
    }
    if (c === 0x28 /* ( */) {
      levels.push(undefined);
      i += 1;
      continue;
    }
    if (c === 0x29 /* ) */) {
      if (levels.length > 1) levels.pop();
      i += 1;
      continue;
    }
    // Variable / bind / temp-object sigils: consume the whole attached name so
    // `@set` / `:set` / `#set` never shadow the tracked keywords.
    if (c === 0x40 /* @ */ || c === 0x3a /* : */ || c === 0x23 /* # */) {
      i += 1;
      while (i < until && /[A-Za-z0-9_$@#]/.test(source[i]!)) i += 1;
      continue;
    }
    if (/[A-Za-z_]/.test(source[i]!)) {
      let j = i + 1;
      while (j < until && /[A-Za-z0-9_$]/.test(source[j]!)) j += 1;
      const word = source.slice(i, j).toLowerCase();
      if (levels.length === 1) {
        if (head === undefined) head = word;
        else if (head === 'with' && CTE_BODY_HEADS.has(word)) head = word;
      }
      if (TRACKED_KEYWORDS.has(word)) levels[levels.length - 1] = word;
      i = j;
      continue;
    }
    i += 1;
  }
  if (levels[levels.length - 1] !== 'set') return 'other';
  if (head !== undefined && DML_SET_HEADS.has(head)) return 'set-assignment';
  return head === 'set' ? 'non-dml-set' : 'other';
}
